import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Router, RouterLink } from '@angular/router';
import { AppStateService } from '../../../services/app-state.service';
import { ApiCmdService } from '../../../services/api-cmd.service';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { DataListBackofficeUsersApiResponseDto, QueryListBackofficeUsersApiRequestDto } from '@packages/api-cmd-type-definitions';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { FiltersBackofficeUsersComponent } from '../filters/filters-backoffice-users.component';
import { GetBackofficeUserResponse } from '@packages/api-cmd-type-definitions/lib/backoffice-users/get-backoffice-user.dto';
import { MatSidenavContent } from '@angular/material/sidenav';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { getIsEnabledLabel, getRoleLabel } from '../../../constants/backoffice-user.constants';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgIf } from '@angular/common';
import { HasRoleModule } from '../../../modules/has-role.module';
import { MatListItem } from '@angular/material/list';
import { handlerSnackbarApiError } from '../../../utils/snackbar.handler';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-utenti-backoffice',
  standalone: true,
  imports: [
    MatSidenavContent,
    MatButton,
    RouterLink,
    MatTable,
    MatSort,
    MatHeaderCell,
    MatColumnDef,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRowDef,
    MatRowDef,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    MatSortHeader,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    NgIf,
    HasRoleModule,
    MatListItem,
  ],
  templateUrl: './list-backoffice-users.component.html',
  styleUrl: './list-backoffice-users.component.scss',
})
export class ListBackofficeUsersComponent implements OnInit, OnDestroy {
  public router = inject(Router);
  public appStateService = inject(AppStateService);
  private apiCmdService = inject(ApiCmdService);
  displayedColumns: string[] = ['email', 'firstName', 'lastName', 'role', 'isEnabled'];
  dataSource = new MatTableDataSource<any>([]);
  paginationSkip = 0;
  paginationLimit = 32;
  sortField = 'firstName';
  sortDirection: 'asc' | 'desc' = 'asc';
  public $legalEntityCodeSelected = toObservable(computed(() => this.appStateService.$legalEntityCodeSelected())).subscribe({
    next: value => {
      this.clearTable();
      this.loadData();
    },
    error: err => {
      console.error(err);
    },
  });
  private scrollSubscription: Subscription = new Subscription();
  private queryFilters: QueryListBackofficeUsersApiRequestDto = {};
  protected readonly getRoleLabel = getRoleLabel;
  protected readonly getIsEnabledLabel = getIsEnabledLabel;
  public isLoading = true;

  constructor(
    public dialog: MatDialog,
    private snackbar: MatSnackBar
  ) {}

  private setterIsLoading(value: boolean): () => void {
    return () => {
      this.isLoading = value;
    };
  }

  ngOnInit() {
    //this.loadData();
  }

  ngOnDestroy() {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
    this.resetSort();
    this.clearTable();
  }

  navigateToCreate() {
    this.router.navigate(['/utenti-backoffice/create']);
  }

  refreshList() {
    this.clearTable();
    this.loadData();
  }

  openFilterDialog(): void {
    const dialogRef = this.dialog.open(FiltersBackofficeUsersComponent, { data: this.queryFilters });
    dialogRef.componentInstance.filtersApplied.subscribe((filters: any) => {
      this.applyFilters(filters);
      if (Object.keys(filters).length > 0) {
        this.clearTable();
        this.loadData();
      }
    });
  }

  clearTable() {
    this.paginationSkip = 0;
    this.dataSource.data = [];
  }
  resetSort() {
    this.sortField = 'firstName';
    this.sortDirection = 'asc';
  }

  applyFilters(filters: Record<string, any>): void {
    Object.keys(filters).forEach(attribute => {
      if (filters[attribute] === null || filters[attribute] === undefined || filters[attribute] === '') {
        // @ts-expect-error TS7053
        delete this.queryFilters[attribute];
      } else {
        // @ts-expect-error TS7053
        this.queryFilters[attribute] = filters[attribute];
      }
    });
  }

  getFilters(): QueryListBackofficeUsersApiRequestDto {
    if (this.queryFilters.isEnabled === undefined) {
      delete this.queryFilters.isEnabled;
      return this.queryFilters;
    }
    return {
      ...this.queryFilters,
      isEnabled: this.queryFilters.isEnabled ? '1' : '0',
    };
  }

  loadData() {
    this.isLoading = true;
    this.apiCmdService
      .listBackofficeUsers({
        ...this.getFilters(),
        paginationSkip: this.paginationSkip,
        paginationLimit: this.paginationLimit,
        sortOrder: this.sortDirection,
        fieldOrder: this.sortField,
      })
      .subscribe({
        next: (response: DataListBackofficeUsersApiResponseDto) => {
          this.dataSource.data = [
            ...this.dataSource.data,
            ...response.data.map((item: GetBackofficeUserResponse): Omit<GetBackofficeUserResponse, 'legalEntityCodes'> & { legalEntityCodes: string } => {
              return { ...item, legalEntityCodes: item.legalEntityCodes.join(' | ') };
            }),
          ];
          this.isLoading = false;
        },
        error: err => handlerSnackbarApiError(this.snackbar, err, [this.setterIsLoading(false)]),
      });
  }

  loadMoreData(event: any) {
    this.paginationSkip += this.paginationLimit;
    this.loadData();
  }

  sortData(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'asc';
    this.clearTable();
    this.loadData();
  }

  onRowClick(event: MouseEvent, row: any) {
    if (window.getSelection()?.toString()) {
      return; // Do nothing if text is selected
    }

    this.router.navigate(['/utenti-backoffice/edit', row.email]);
  }

  onMouseUp(event: MouseEvent) {
    // This method is just to capture the mouseup event
  }
}
